import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Button } from '@/components/common/Button';

export const PageLayout = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.background};
  padding: ${({ theme }) => theme.spacing.xl};
  gap: ${({ theme }) => theme.spacing.lg};

  @media (max-width: 768px) {
    overflow: visible;
    padding: ${({ theme }) => `${theme.spacing.lg} ${theme.spacing.md}`};
  }

  @media (max-width: 640px) {
    padding: ${({ theme }) => `${theme.spacing.md} ${theme.spacing.sm}`};
  }

  @media (max-width: 480px) {
    padding: ${({ theme }) => theme.spacing.sm};
  }
`;

export const ContentLayout = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;
  position: relative;

  @media (max-width: 1024px) {
    overflow: visible;
  }

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export const MainContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.surface};
  padding: ${({ theme }) => theme.spacing.lg};

  @media (max-width: 1024px) {
    padding: ${({ theme }) => theme.spacing.md};
  }

  @media (max-width: 768px) {
    align-items: center;
    justify-content: center;
    overflow: visible;
  }

  @media (max-width: 640px) {
    padding: ${({ theme }) => theme.spacing.sm};
    align-items: center;
  }
`;

export const Sidebar = styled(motion.div)`
  position: fixed;
  right: 0;
  top: 0;
  bottom: 0;
  width: min(400px, 100%);
  background: ${({ theme }) => 
    theme.colors.background === '#0f172a' 
      ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.98) 0%, rgba(15, 23, 42, 0.98) 100%)'
      : 'rgba(255, 255, 255, 0.95)'};
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-left: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 1000;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.1);

  @media (max-width: 1024px) {
    width: 360px;
  }

  @media (max-width: 768px) {
    /* On mobile, keep the drawer as a floating overlay that
       covers the height from the top, rather than turning it
       into an inline panel at the bottom. */
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    width: 100%;
    max-width: 100%;
    border-radius: 0;
  }
`;

export const ToggleDrawerButton = styled(Button)`
  margin-left: auto;
  width: 44px;
  height: 44px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

export const SidebarHeader = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.md};

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing.md};
  }

  @media (max-width: 480px) {
    padding: ${({ theme }) => theme.spacing.sm};
    flex-wrap: wrap;
  }
`;

export const SidebarTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  color: ${({ theme }) => theme.colors.onBackground};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  margin: 0;
  flex: 1;
`;

export const DrawerCloseButton = styled(motion.button)`
  width: 36px;
  height: 36px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  color: ${({ theme }) => theme.colors.onBackground};
  flex-shrink: 0;

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }

  &:active {
    transform: scale(0.95);
  }
`;

export const SidebarContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing.lg};

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing.md};
  }

  @media (max-width: 480px) {
    padding: ${({ theme }) => theme.spacing.sm};
  }
`;

export const ExplorerWrapper = styled.div`
  width: 100%;
  min-height: 100%;
  display: flex;
  justify-content: center;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing.lg};
  box-sizing: border-box;

  @media (max-width: 1024px) {
    padding: ${({ theme }) => theme.spacing.md};
  }

  @media (max-width: 768px) {
    padding: ${({ theme }) => `${theme.spacing.md} ${theme.spacing.sm}`};
  }

  @media (max-width: 640px) {
    padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.xs}`};
  }

  @media (max-width: 480px) {
    padding: 0;
  }
`;

export const ExplorerContent = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding-bottom: ${({ theme }) => theme.spacing.lg};

  @media (max-width: 768px) {
    padding-left: ${({ theme }) => theme.spacing.sm};
    padding-right: ${({ theme }) => theme.spacing.sm};
  }

  @media (max-width: 640px) {
    padding-left: 0;
    padding-right: 0;
  }
`;

export const DocumentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => `${theme.spacing.sm} 0`};

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

export const Header = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: clamp(0.5rem, 2vw, 1rem);
  margin-bottom: clamp(0.75rem, 2vw, 1.5rem);
  flex-shrink: 0;
  flex-wrap: wrap;
`;

export const BackButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};

  @media (max-width: 480px) {
    padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
    font-size: ${({ theme }) => theme.fontSizes.sm};

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

export const Title = styled.h1`
  font-size: clamp(1.25rem, 3vw, 1.875rem);
  color: ${({ theme }) => theme.colors.onBackground};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  flex: 1;
`;

export const DocumentCount = styled.span`
  background: ${({ theme }) => theme.colors.primary}15;
  color: ${({ theme }) => theme.colors.primary};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
`;

export const DocumentCard = styled(motion.div)<{ isSelected?: boolean }>`
  background: ${({ theme, isSelected }) => 
    isSelected ? theme.colors.primary + '15' : theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  border: 1px solid ${({ theme, isSelected }) => 
    isSelected ? theme.colors.primary : theme.colors.border};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};

  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.md};
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

export const FileIcon = styled.div<{ $fileType: string }>`
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ $fileType }) => {
    switch ($fileType) {
      case 'pdf':
        return '#ef444415';
      case 'doc':
        return '#2563eb15';
      case 'xls':
        return '#10b98115';
      case 'ppt':
        return '#f59e0b15';
      default:
        return '#64748b15';
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 24px;
    height: 24px;
    color: ${({ $fileType }) => {
      switch ($fileType) {
        case 'pdf':
          return '#ef4444';
        case 'doc':
          return '#2563eb';
        case 'xls':
          return '#10b981';
        case 'ppt':
          return '#f59e0b';
        default:
          return '#64748b';
      }
    }};
  }
`;

export const DocumentInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const DocumentTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes.base};
  color: ${({ theme }) => theme.colors.onBackground};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const DocumentDescription = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.muted};
  margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const DocumentMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  // gap: ${({ theme }) => theme.spacing.lg};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.muted};
  width: 100%;
`;

export const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};

  svg {
    width: 14px;
    height: 14px;
  }
`;

export const FileSize = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.muted};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  margin-left: auto;
`;

export const DocumentActions = styled.div`
  display: inline-flex;
  margin-left: ${({ theme }) => theme.spacing.sm};

  a {
    display: inline-flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.xs};
    font-size: ${({ theme }) => theme.fontSizes.xs};
    font-weight: ${({ theme }) => theme.fontWeights.semibold};
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: none;
    border: 1px solid ${({ theme }) => theme.colors.primary}30;
    border-radius: ${({ theme }) => theme.borderRadius.full};
    padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
    transition: ${({ theme }) => theme.transitions.default};

    svg {
      width: 14px;
      height: 14px;
    }

    &:hover {
      background: ${({ theme }) => theme.colors.primary}10;
      border-color: ${({ theme }) => theme.colors.primary};
    }
  }
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing['3xl']};
  color: ${({ theme }) => theme.colors.muted};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme}) => theme.spacing.lg};
`;

export const EmptyIcon = styled.div`
  font-size: 4rem;
  opacity: 0.5;
`;

export const EmptyTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  color: ${({ theme }) => theme.colors.onBackground};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  margin: 0;
`;

export const EmptyText = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.base};
  color: ${({ theme }) => theme.colors.muted};
  margin: 0;
  max-width: 400px;
`;

export const AccordionSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  background: ${({ theme }) => theme.colors.background};
`;

export const AccordionHeader = styled.div<{ $isExpanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.lg};
  cursor: pointer;
  background: ${({ theme, $isExpanded }) => 
    $isExpanded ? theme.colors.surface : theme.colors.background};
  transition: ${({ theme }) => theme.transitions.default};
  border-bottom: ${({ theme, $isExpanded }) => 
    $isExpanded ? `1px solid ${theme.colors.border}` : 'none'};

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
  }

  svg {
    width: 20px;
    height: 20px;
    color: ${({ theme }) => theme.colors.primary};
    transition: ${({ theme }) => theme.transitions.default};
  }
`;

export const AccordionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.fontSizes.base};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.onBackground};
`;

export const AccordionContent = styled(motion.div)`
  overflow: hidden;
  padding: ${({ theme }) => theme.spacing.md};

  @media (max-width: 480px) {
    padding: ${({ theme }) => theme.spacing.sm};
  }
`;

export const FlipbookViewerContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.lg};

  @media (max-width: 1024px) {
    padding: ${({ theme }) => theme.spacing.md};
  }

  @media (max-width: 768px) {
    padding-bottom: ${({ theme }) => theme.spacing['3xl'] || theme.spacing['2xl']};
  }

  @media (max-width: 640px) {
    padding: ${({ theme }) => theme.spacing.sm};
  }
`;

export const FlipControlsPanel = styled.div`
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) =>
    theme.colors.background === '#0f172a'
      ? 'rgba(15, 23, 42, 0.96)'
      : 'rgba(255, 255, 255, 0.96)'};
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadows.md};
  z-index: 20;
  width: auto;
  min-width: 60px;
  min-height: auto;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);

  @media (max-width: 1024px) {
    right: 12px;
  }

  @media (max-width: 768px) {
    position: fixed;
    left: 50%;
    right: auto;
    bottom: ${({ theme }) => theme.spacing.sm};
    top: auto;
    transform: translateX(-50%);
    flex-direction: row;
    width: calc(100% - 32px);
    max-width: 480px;
    justify-content: center;
    gap: ${({ theme }) => theme.spacing.sm};
    margin-top: 0;
  }
`;

export const FlipControlsRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: center;
  }
`;

export const FlipSearchWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

export const FlipIconButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.onBackground};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: ${({ theme }) => theme.transitions.default};
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.primary};
    border-color: ${({ theme }) => theme.colors.primary};
    color: #ffffff;
    transform: scale(1.05);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const FlipPageInfo = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.muted};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  text-align: center;
  min-width: 50px;
`;

export const FlipPageInput = styled.input`
  width: 100%;
  padding: 8px 6px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.onBackground};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  text-align: center;
  transition: ${({ theme }) => theme.transitions.default};

  /* Hide default number input arrows */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type='number'] {
    -moz-appearance: textfield;
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary}33;
    background: ${({ theme }) => theme.colors.surface};
  }
`;

export const FlipSearchInput = styled(FlipPageInput)`
  flex: 1;
  text-align: left;
`;

export const FlipSearchPopover = styled(motion.div)`
  position: absolute;
  right: calc(100% + 25px);
  top: 0%;
  transform: translateY(-50%);
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) =>
    theme.colors.background === '#0f172a'
      ? 'rgba(15, 23, 42, 0.98)'
      : 'rgba(255, 255, 255, 0.98)'};
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadows.lg || theme.shadows.md};
  width: 320px;
  max-height: 360px;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  z-index: 30;

  /* On mobile, show the search popover above the floating controls bar */
  @media (max-width: 768px) {
    left: 50%;
    right: auto;
    top: auto;
    bottom: calc(100% + ${({ theme }) => theme.spacing.xs});
    transform: translateX(-50%);
    width: 100%;
    max-width: 480px;
  }
`;

export const FlipSearchResults = styled.div`
  margin-top: ${({ theme }) => theme.spacing.sm};
  max-height: 260px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`;

export const FlipSearchResultItem = styled.button<{ $isActive?: boolean }>`
  width: 100%;
  text-align: left;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid
    ${({ theme, $isActive }) => ($isActive ? theme.colors.primary : theme.colors.border)};
  background: ${({ theme, $isActive }) =>
    $isActive ? theme.colors.primary + '15' : theme.colors.surface};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 4px;
  transition: ${({ theme }) => theme.transitions.default};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primary}10;
  }
`;

export const FlipSearchResultTitle = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.onBackground};
`;

export const FlipSearchResultSnippet = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.muted};
`;
